import { Eye, EyeOff, MapPin } from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrgSettings, useUpdateOrgSetting } from "@/hooks/use-org-settings";

export function IntegrationsPage() {
  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <PageHeader
        title="Integrations"
        description="Manage API keys and third-party service connections"
      />
      <GoogleMapsCard />
    </div>
  );
}

function GoogleMapsCard() {
  const { data: settings, isLoading } = useOrgSettings();
  const update = useUpdateOrgSetting();
  const [value, setValue] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  const saved = settings?.google_maps_api_key ?? "";
  const current = value ?? saved;

  function handleSave() {
    update.mutate({ key: "google_maps_api_key", value: current });
  }

  const isDirty = value !== null && value !== saved;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-50 rounded-lg">
          <MapPin className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">Google Maps</h3>
          <p className="text-sm text-gray-500">
            Used to display interactive property maps on the Project Overview page
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="maps-key">Maps Embed API Key</Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              id="maps-key"
              type={showKey ? "text" : "password"}
              placeholder={isLoading ? "Loading…" : "AIza…"}
              value={current}
              onChange={(e) => setValue(e.target.value)}
              className="pr-10 font-mono text-sm"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600"
              aria-label={showKey ? "Hide key" : "Show key"}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <Button onClick={handleSave} disabled={!isDirty || update.isPending} className="shrink-0">
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
        <p className="text-xs text-gray-400">
          Requires the <strong>Maps Embed API</strong> to be enabled in your Google Cloud project.
          The key is stored securely in the database and never sent to the browser.
        </p>
      </div>

      {saved && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          API key configured
        </div>
      )}
    </div>
  );
}
